import React, { useEffect, useState } from 'react';
import { Download, X, Share, PlusSquare, Flame } from 'lucide-react';

const DISMISS_KEY = 'atf_install_dismissed';
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

export const InstallPWA = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [showIosSheet, setShowIosSheet] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    const dismissed = localStorage.getItem(DISMISS_KEY) === '1';

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (!dismissed) setShow(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);

    const onManual = () => {
      if (isStandalone()) return;
      setShow(true);
      if (isIOS()) setShowIosSheet(true);
    };
    window.addEventListener('atf-open-install', onManual);

    let t;
    if (isIOS() && !dismissed) {
      t = setTimeout(() => setShow(true), 2500);
    }
    return () => {
      if (t) clearTimeout(t);
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('atf-open-install', onManual);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    setShowIosSheet(false);
    localStorage.setItem(DISMISS_KEY, '1');
  };

  const install = async () => {
    if (isIOS()) {
      setShowIosSheet(true);
      return;
    }
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === 'accepted') {
      setShow(false);
      localStorage.setItem(DISMISS_KEY, '1');
    }
    setDeferred(null);
  };

  if (!show) return null;

  return (
    <>
      <div
        className="fixed bottom-4 inset-x-4 md:inset-x-auto md:right-6 md:bottom-6 md:w-[380px] z-[60] animate-fade-up"
        data-testid="install-banner"
      >
        <div className="card-dark rounded-2xl border border-fire-gold/30 shadow-2xl p-4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-fire-cream flex items-center justify-center shrink-0 overflow-hidden">
            <img src="/icon-192.png" alt="After the Fire app" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display text-fire-cream text-base leading-tight">Get the After the Fire™ app</p>
            <p className="font-body text-fire-cream/60 text-xs mt-0.5">Add it to your home screen for quick access.</p>
          </div>
          <button
            onClick={install}
            data-testid="install-app-button"
            className="btn-gold rounded-full px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 shrink-0"
          >
            <Download className="w-3.5 h-3.5" /> Install
          </button>
          <button onClick={dismiss} aria-label="Dismiss" data-testid="install-dismiss" className="p-1.5 text-fire-cream/50 hover:text-fire-cream shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {showIosSheet && (
        <div className="fixed inset-0 z-[70] flex items-end md:items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setShowIosSheet(false)}>
          <div className="card-dark rounded-2xl border border-fire-gold/30 max-w-sm w-full p-6 text-center" onClick={(e) => e.stopPropagation()} data-testid="ios-install-sheet">
            <Flame className="w-8 h-8 text-fire-gold mx-auto mb-3 animate-flicker" strokeWidth={1.4} />
            <h3 className="font-display text-xl text-fire-cream mb-1">Install on your iPhone</h3>
            <p className="font-body text-fire-cream/60 text-sm mb-6">Add After the Fire™ to your home screen in two taps.</p>
            <div className="space-y-3 text-left">
              <div className="flex items-center gap-3 bg-fire-ink/60 rounded-xl px-4 py-3">
                <Share className="w-5 h-5 text-fire-goldlight shrink-0" />
                <p className="font-body text-fire-cream/85 text-sm">1. Tap the <span className="text-fire-goldlight">Share</span> button in Safari's toolbar</p>
              </div>
              <div className="flex items-center gap-3 bg-fire-ink/60 rounded-xl px-4 py-3">
                <PlusSquare className="w-5 h-5 text-fire-goldlight shrink-0" />
                <p className="font-body text-fire-cream/85 text-sm">2. Choose <span className="text-fire-goldlight">Add to Home Screen</span></p>
              </div>
            </div>
            <button onClick={() => setShowIosSheet(false)} className="btn-outline-gold rounded-full px-6 py-2.5 text-sm font-semibold mt-6 w-full" data-testid="ios-sheet-close">
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPWA;
